import React, { useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { JsonPatchDocument } from '@matech/thebigpos-sdk'
import { LoanSettings } from '@matech/thebigpos-sdk/src'
import * as yup from 'yup'
import { ObjectSchema } from 'yup'
import { useIntl } from 'react-intl'
import useForm from '../../../../hooks/useForm'
import { useLoanContext } from '../../../../contexts/LoanContext'
import { TheBigPOSApi } from '../../../../lib/TheBigPOSClient'
import queryKeys from '../../../../services/queryKeys'
import Switch from '../../../../components/formControls/Switch'
import { RFHControl } from '../../../../types'
import { Button } from '../../../../components/Button'
import FormRow from '../../../../components/formControls/FormRow'
import { useAlert } from '../../../../hooks'
import { getErrorMessage } from '../../../../services/helper'

export type LoanSettingsFieldValues = LoanSettings

const schema: ObjectSchema<LoanSettingsFieldValues> = yup
	.object()
	.shape({
		excludeFromAutoTaskReminders: yup.boolean().required(),
	})

const LoanSettingsForm = () => {
	const { formatMessage } = useIntl()
	const { alert } = useAlert()
	const { loan, loanId } = useLoanContext()
	const queryClient = useQueryClient()
	const methods = useForm<LoanSettingsFieldValues>({
		defaultValues: {
			...loan?.loanSettings,
		},
		schema,
	})

	const { mutate: updateLoanSettings } = useMutation({
		mutationFn: async (data: LoanSettingsFieldValues) => {
			if (!loanId) {
				throw new Error(
					formatMessage({ id: 'loan_settings.missing_loan_id' })
				)
			}
			const patchDoc: JsonPatchDocument = []

			Object.entries(data).forEach(([key, value]) => {
				if (value !== undefined) {
					patchDoc.push({
						op: 'replace',
						path: `/loanSettings/${key}`,
						value,
					})
				}
			})

			return TheBigPOSApi.updateLoan(loanId, patchDoc) // returns updated LoanDto
		},

		onSuccess: (updatedLoan) => {
			queryClient.setQueryData([queryKeys.loan, loanId], updatedLoan)
			alert(formatMessage({ id: 'loan_settings.update_success' }), {
				severity: 'success',
			})
		},
		onError: (error) => {
			alert(getErrorMessage(error), { severity: 'error' })
		},
	})

	const handleFormSubmit = (data: LoanSettingsFieldValues) => {
		updateLoanSettings(data)
	}

	useEffect(() => {
		methods.reset({
			...loan?.loanSettings,
		})
	}, [loan?.loanSettings, methods])

	return (
		<div>
			<form onSubmit={methods.handleSubmit(handleFormSubmit)}>
				<FormRow>
					<Switch
						name="excludeFromAutoTaskReminders"
						label={formatMessage({
							id: 'loan_settings.exclude_from_auto_task_reminders',
						})}
						control={methods.control as RFHControl}
					/>
				</FormRow>
				<FormRow>
					<Button
						text={formatMessage({ id: 'global.save' })}
						type="submit"
						disabled={!methods.formState.isDirty}
					/>
				</FormRow>
			</form>
		</div>
	)
}

export default LoanSettingsForm
